import { useState } from "react";
import { Copy, ChevronRight, ChevronDown, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Shared links supply these values, so quotes inside them must remain literal shell arguments.
function shellQuote(value: string): string {
  return `'${value.replace(/'/g, "'\"'\"'")}'`;
}

interface ConnectDialogProps {
  isOpen: boolean;
  instanceId: string;
  apiUrl?: string;
  adbWebSocketUrl?: string;
  mcpUrl?: string;
  token?: string;
  onOpenChange: (isOpen: boolean) => void;
}

export function ConnectDialog({
  isOpen,
  instanceId,
  apiUrl,
  adbWebSocketUrl,
  mcpUrl,
  token,
  onOpenChange,
}: ConnectDialogProps) {
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [copiedMcpUrl, setCopiedMcpUrl] = useState(false);
  const [copiedAuthHeader, setCopiedAuthHeader] = useState(false);
  const [copiedClaudeCommand, setCopiedClaudeCommand] = useState(false);
  const [isClaudeCodeExpanded, setIsClaudeCodeExpanded] = useState(false);
  const [isCustomExpanded, setIsCustomExpanded] = useState(false);

  const isIos = instanceId.startsWith("ios_");
  const isXcode = instanceId.startsWith("sandbox_");
  const isGradle = instanceId.startsWith("gradle_");
  const cliType = isIos ? "ios" : isXcode ? "xcode" : isGradle ? "gradle" : "android";
  const isAndroid = cliType === "android";
  // Android also stores the ADB WebSocket URL so a keyless `lim android
  // connect` can open the tunnel afterwards.
  const setInstanceCommand =
    apiUrl && token
      ? `lim ${cliType} set-instance \\\n  --api-url ${shellQuote(apiUrl)} \\\n  --token ${shellQuote(token)}` +
        (isAndroid && adbWebSocketUrl ? ` \\\n  --adb-websocket-url ${shellQuote(adbWebSocketUrl)}` : "") +
        (isAndroid ? `\nlim android connect` : "")
      : undefined;
  const command =
    setInstanceCommand ??
    (isXcode
      ? `lim xcode build . --id ${shellQuote(instanceId)}`
      : isGradle
        ? `lim gradle build . --id ${shellQuote(instanceId)}`
        : isAndroid
          ? `lim android connect --id ${shellQuote(instanceId)}`
          : undefined);
  const commandDescription = setInstanceCommand
    ? `Use this instance with the Limrun CLI. Run the command below once to connect.` +
      (isAndroid ? " The second command opens a tunnel for local ADB." : "") +
      (isXcode
        ? " Then lim xcode build . syncs and builds. Add --scheme or --workspace if your project needs them."
        : "") +
      (isGradle
        ? " Then lim gradle build . syncs and runs the Gradle wrapper. Add --task for a task other than the default."
        : "")
    : isXcode
      ? "Sync the current directory and build it on this Xcode sandbox. Add --scheme or --workspace if your project needs them."
      : isGradle
        ? "Sync the current directory and run its Gradle wrapper on this gradle sandbox. Add --task for a task other than the default."
        : isAndroid
          ? "Open an ADB tunnel so your local ADB can talk to this Android emulator."
          : "Pin this instance so subsequent lim ios commands target it.";
  const authHeader = token ? `Authorization: Bearer ${token}` : undefined;
  const claudeCodeCommand =
    mcpUrl && token
      ? `claude mcp add simulator --transport http \\\n${shellQuote(mcpUrl)} \\\n--header ${shellQuote(`Authorization: Bearer ${token}`)}`
      : undefined;

  const mcpName = instanceId.startsWith("ios_")
    ? "iOS Simulator"
    : instanceId.startsWith("sandbox_")
      ? "Xcode"
      : "Android Simulator";
  const cursorMcpConfig =
    mcpUrl && token
      ? {
          url: mcpUrl,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
  const replitMcpConfig =
    mcpUrl && token
      ? {
          displayName: mcpName,
          baseUrl: mcpUrl,
          headers: [
            {
              key: "Authorization",
              value: `Bearer ${token}`,
            },
          ],
        }
      : null;
  const replitButton = replitMcpConfig ? (
    <Button asChild variant="outline">
      <a
        href={`https://replit.com/integrations?mcp=${encodeURIComponent(btoa(JSON.stringify(replitMcpConfig)))}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Add to Replit <ArrowUpRight />
      </a>
    </Button>
  ) : null;
  const cursorButton = cursorMcpConfig ? (
    <Button asChild variant="outline">
      <a
        href={`cursor://anysphere.cursor-deeplink/mcp/install?name=${mcpName}&config=${encodeURIComponent(btoa(JSON.stringify(cursorMcpConfig)))}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Add to Cursor <ArrowUpRight />
      </a>
    </Button>
  ) : null;
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Connect</DialogTitle>
          <DialogDescription>
            Connect your terminal or coding agent to this instance.
          </DialogDescription>
        </DialogHeader>
        <section className="min-w-0 space-y-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">Command line</h3>
            <p className="text-sm leading-6 text-muted-foreground">{commandDescription}</p>
          </div>
          {command && (
            <div className="flex min-w-0 items-start justify-between gap-3 rounded-md border bg-muted/40 p-3">
              <pre className="min-w-0 flex-1">
                <code className="whitespace-pre-wrap break-all text-xs leading-5">{command}</code>
              </pre>
              <TooltipProvider>
                <Tooltip delayDuration={0} open={copiedCommand}>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Copy connection command"
                      size="sm"
                      variant="ghost"
                      className="h-7 shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(command);
                        setCopiedCommand(true);
                        setTimeout(() => setCopiedCommand(false), 1000);
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copied!</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          <p className="text-xs leading-5 text-muted-foreground">
            Install the CLI with <code className="text-foreground">npm install -g lim</code>.
          </p>
        </section>
        {mcpUrl && (
          <div className="min-w-0 space-y-4 border-t pt-5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Coding agents</h3>
              <p className="text-sm leading-6 text-muted-foreground">
                Connect through this instance's MCP server.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {cursorButton}
              {replitButton}
            </div>
            {claudeCodeCommand && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm py-1 text-left text-sm font-medium transition-colors hover:text-foreground"
                  aria-expanded={isClaudeCodeExpanded}
                  onClick={() => setIsClaudeCodeExpanded(!isClaudeCodeExpanded)}
                >
                  {isClaudeCodeExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  Claude Code
                </button>
                {isClaudeCodeExpanded && (
                  <div className="flex min-w-0 items-start justify-between gap-3 rounded-md border bg-muted/40 p-3">
                    <pre className="min-w-0 flex-1">
                      <code className="whitespace-pre-wrap break-all text-xs leading-5">
                        {claudeCodeCommand}
                      </code>
                    </pre>
                    <TooltipProvider>
                      <Tooltip delayDuration={0} open={copiedClaudeCommand}>
                        <TooltipTrigger asChild>
                          <Button
                            aria-label="Copy Claude Code command"
                            size="sm"
                            variant="ghost"
                            className="h-7 shrink-0"
                            onClick={() => {
                              navigator.clipboard.writeText(claudeCodeCommand);
                              setCopiedClaudeCommand(true);
                              setTimeout(() => setCopiedClaudeCommand(false), 1000);
                            }}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copied!</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm py-1 text-left text-sm font-medium transition-colors hover:text-foreground"
                aria-expanded={isCustomExpanded}
                onClick={() => setIsCustomExpanded(!isCustomExpanded)}
              >
                {isCustomExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Manual configuration
              </button>
              {isCustomExpanded && (
                <div className="space-y-3 pl-4">
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">URL</p>
                    <div className="flex min-w-0 items-start justify-between gap-3 rounded-md border bg-muted/40 p-3">
                      <pre className="min-w-0 flex-1">
                        <code className="whitespace-pre-wrap break-all text-xs leading-5">
                          {mcpUrl}
                        </code>
                      </pre>
                      <TooltipProvider>
                        <Tooltip delayDuration={0} open={copiedMcpUrl}>
                          <TooltipTrigger asChild>
                            <Button
                              aria-label="Copy MCP URL"
                              size="sm"
                              variant="ghost"
                              className="h-7 shrink-0"
                              onClick={() => {
                                navigator.clipboard.writeText(mcpUrl);
                                setCopiedMcpUrl(true);
                                setTimeout(() => setCopiedMcpUrl(false), 1000);
                              }}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copied!</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  {authHeader && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">HTTP Header</p>
                      <div className="flex min-w-0 items-start justify-between gap-3 rounded-md border bg-muted/40 p-3">
                        <pre className="min-w-0 flex-1">
                          <code className="whitespace-pre-wrap break-all text-xs leading-5">
                            {authHeader}
                          </code>
                        </pre>
                        <TooltipProvider>
                          <Tooltip delayDuration={0} open={copiedAuthHeader}>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label="Copy authorization header"
                                size="sm"
                                variant="ghost"
                                className="h-7 shrink-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(authHeader);
                                  setCopiedAuthHeader(true);
                                  setTimeout(() => setCopiedAuthHeader(false), 1000);
                                }}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copied!</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
